import type { TFunction } from 'i18next';
import { Position, type Edge, type Node } from '@xyflow/react';
import type { TreeFlowNodeType } from '@admin/components/flow/tree-flow-node.tsx';
import type { TreeFlowDeleteEdgeType } from '@admin/components/flow/tree-flow-delete-edge.tsx';
import type { TreeFlowLeafClusterNodeType } from '@admin/components/flow/tree-flow-leaf-cluster-node.tsx';
import {
  layoutTreeForest,
  leafClusterNodeId,
  type TreeFlowPosition
} from '@admin/components/flow/tree-flow-layout.ts';
import type { HierarchyFlowLink, HierarchyFlowNode } from './sales-hierarchy-flow-utils.ts';

interface BuildHierarchyCanvasGraphParams {
  allNodes: HierarchyFlowNode[];
  links: HierarchyFlowLink[];
  canManage: boolean;
  deletingLinkId: number | null;
  stagedPositions: Map<string, TreeFlowPosition>;
  treeRootIds: Set<string>;
  t: TFunction;
  onTrash: (mode: 'trash' | 'cut', linkId: number, target: string) => void;
  onRemoveFromCanvas: (nodeId: string) => void;
}

/**
 * Construit les `nodes`/`edges` React Flow à partir de l'arbre applicatif :
 * layout dagre, clustering des feuilles, câblage des actions corbeille/
 * ciseaux par nœud. Pure (aucun hook) — extrait du `useMemo` de
 * `SalesHierarchyCanvas` (composant devenu trop volumineux), appelé tel quel
 * depuis son `useMemo` avec les mêmes dépendances.
 */
export function buildHierarchyCanvasGraph({
  allNodes,
  links,
  canManage,
  deletingLinkId,
  stagedPositions,
  treeRootIds,
  t,
  onTrash,
  onRemoveFromCanvas
}: BuildHierarchyCanvasGraphParams): { nodes: Node[]; edges: Edge[] } {
  const flatNodes = allNodes.map((node) => ({ id: node.id, parentId: node.parentId }));
  const { positions, leafClusters } = layoutTreeForest(flatNodes, {
    verticalGap: 180,
    horizontalGap: 60,
    orientation: 'vertical'
  });

  // Un nœud déposé depuis la sidebar a une position figée au point de drop
  // (cf. `handleExternalDrop`), pas celle calculée par dagre (qui le
  // traiterait comme une nouvelle tête de forêt, sans rapport avec l'endroit
  // où l'utilisateur l'a lâché).
  for (const [id, position] of stagedPositions) {
    if (allNodes.some((node) => node.id === id)) positions.set(id, position);
  }

  const clusteredLeafIds = new Set<string>();
  for (const leafIds of leafClusters.values()) {
    for (const leafId of leafIds) clusteredLeafIds.add(leafId);
  }

  const nodeById = new Map(allNodes.map((node) => [node.id, node]));
  const linkByTarget = new Map(links.map((link) => [link.target, link]));

  const directSubordinatesCount = new Map<string, number>();
  for (const node of allNodes) {
    if (node.parentId === null) continue;
    directSubordinatesCount.set(
      node.parentId,
      (directSubordinatesCount.get(node.parentId) ?? 0) + 1
    );
  }

  const positionedNodes: Node[] = [];

  for (const node of allNodes) {
    if (clusteredLeafIds.has(node.id)) continue; // affichée dans le cluster de son parent

    const count = directSubordinatesCount.get(node.id) ?? 0;
    const incomingLink = linkByTarget.get(node.id);
    // Pas de lien entrant, et pas une vraie tête de hiérarchie (celle-là a des
    // liens réels en dessous) : c'est un nœud purement local (staged, ou
    // détaché via les ciseaux) — sa "corbeille" ne fait qu'un retrait local,
    // aucun appel back (rien à supprimer, il n'a pas de lien).
    const isLocalOnly = !incomingLink && node.parentId === null && !treeRootIds.has(node.id);
    positionedNodes.push({
      id: node.id,
      type: 'hierarchy',
      position: positions.get(node.id) ?? { x: 0, y: 0 },
      data: {
        label: node.label,
        badge: count > 0 ? t('admin.hierarchy.node.subordinates', { count }) : undefined,
        showTargetHandle: true,
        showSourceHandle: true,
        // Déjà un manager : la poignée cible ne doit plus accepter de connexion,
        // 1 seul parent par personne au sein d'une hiérarchie (contrainte back).
        targetConnectable: canManage && node.parentId === null,
        sourceConnectable: canManage,
        targetPosition: Position.Top,
        sourcePosition: Position.Bottom,
        emphasized: treeRootIds.has(node.id),
        onTrash: !canManage
          ? undefined
          : incomingLink
            ? () => onTrash('trash', incomingLink.linkId, node.id)
            : isLocalOnly
              ? () => onRemoveFromCanvas(node.id)
              : undefined,
        trashAriaLabel: incomingLink
          ? t('admin.hierarchy.relation.trash.aria')
          : t('admin.hierarchy.relation.remove.aria'),
        // Rien à couper si déjà sans lien.
        onCut:
          canManage && incomingLink
            ? () => onTrash('cut', incomingLink.linkId, node.id)
            : undefined,
        cutAriaLabel: t('admin.hierarchy.relation.cut.aria'),
        relationActionPending: incomingLink != null && deletingLinkId === incomingLink.linkId
      }
    } satisfies TreeFlowNodeType);
  }

  for (const [parentId, leafIds] of leafClusters) {
    positionedNodes.push({
      id: leafClusterNodeId(parentId),
      type: 'hierarchy-leaf-cluster',
      position: positions.get(leafClusterNodeId(parentId)) ?? { x: 0, y: 0 },
      data: {
        rows: leafIds.map((leafId) => ({
          id: leafId,
          label: nodeById.get(leafId)?.label ?? leafId
        })),
        canActOnRow: canManage,
        actingRowId:
          leafIds.find((leafId) => linkByTarget.get(leafId)?.linkId === deletingLinkId) ?? null,
        trashRowAriaLabel: t('admin.hierarchy.relation.trash.aria'),
        onTrashRow: (rowId) => {
          const link = linkByTarget.get(rowId);
          if (link) onTrash('trash', link.linkId, rowId);
        },
        cutRowAriaLabel: t('admin.hierarchy.relation.cut.aria'),
        onCutRow: (rowId) => {
          const link = linkByTarget.get(rowId);
          if (link) onTrash('cut', link.linkId, rowId);
        },
        rowSourceConnectable: canManage,
        rowSourcePosition: Position.Bottom,
        showTargetHandle: true,
        targetPosition: Position.Top
      }
    } satisfies TreeFlowLeafClusterNodeType);
  }

  const flowEdges: Edge[] = [];

  for (const link of links) {
    if (clusteredLeafIds.has(link.target)) continue; // représenté par l'arête du cluster ci-dessous
    flowEdges.push({
      id: String(link.linkId),
      source: link.source,
      target: link.target,
      type: 'hierarchy-delete'
    } satisfies TreeFlowDeleteEdgeType);
  }

  for (const parentId of leafClusters.keys()) {
    flowEdges.push({
      id: `cluster-${parentId}`,
      source: parentId,
      target: leafClusterNodeId(parentId),
      type: 'hierarchy-delete'
    } satisfies TreeFlowDeleteEdgeType);
  }

  return { nodes: positionedNodes, edges: flowEdges };
}
